import * as React from 'react'

import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { NewAdditionalCompensationsFeatureDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Text from 'modules/core/components/Inputs/Text'
import MoneyField from 'modules/core/components/MoneyField'
import Select from 'modules/core/components/SelectField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'

import { translations as roleLevelSalaryTranslations } from 'modules/contracts/components/FixFlow/lib/roleLevelSalaryTranslations'
import { ContractFlowType } from 'modules/contracts/components/FlowPage'
import FormWrapper from 'modules/contracts/components/FlowPage/FormWrapper'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { getFirstError } from 'modules/contracts/lib/errors'
import { translate } from 'modules/contracts/lib/translate'
import ContractVersion from 'modules/contracts/store/models/ContractVersion'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import AdditionalCompensations from '../_AdditionalCompensations'
import PayrollPolicy from '../_PayrollPolicy'
import SalaryHint from './_SalaryHint'

interface Props {
  form: Form
  step?: number
  withFormWrapper?: boolean
  type?: ContractFlowType
  canEditPayrollPolicy: boolean
  translations?: { [key: string]: string }
}

const Salary = ({
  form,
  translations = roleLevelSalaryTranslations(),
  type = 'fix',
  step,
  canEditPayrollPolicy,
  withFormWrapper,
}: Props) => {
  const { employee, salaryRanges, contractVersion } = useContractFlow()
  const canSeeGrossSalary = policyAuthorizations.for('contracts.see_gross_salary', employee)
  const canSeeCompensations = policyAuthorizations.for(
    'contracts.see_additional_compensation',
    employee
  )
  const canSeePayrollPolicy: boolean = policyAuthorizations.for(
    'contracts.see_payroll_policy',
    employee
  )
  const [isAmountFieldFocused, setIsAmountFieldFocused] = React.useState(false)
  const [isFrequencyFieldFocused, setIsFrequencyFieldFocused] = React.useState(false)
  const jobLevelId = form.get('job_catalog_level_id').value
  const legalEntity = employeeLegalEntity(employee)
  const salaryRange = salaryRanges.filter(
    (x) =>
      x.attributes.legal_entities_ids.includes(legalEntity.attributes.id) &&
      x.attributes.level_id === Number(jobLevelId)
  )[0]

  const { company } = useCurrentSession()
  const {
    data: newAdditionalCompensationsFeatureData,
    error,
    loading,
  } = useQuery(NewAdditionalCompensationsFeatureDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const isNewAdditionalCompensationsSupportEnabled = error
    ? false
    : newAdditionalCompensationsFeatureData?.features?.featuresConnection?.nodes?.[0]?.enabled

  const frequencies = ContractVersion.SalaryFrequencies()
  const salaryOptions: SelectOption[] = Object.keys(frequencies).map((key) => ({
    label: frequencies[key] ?? '',
    value: key,
  }))
  const salaryAmountField = canSeeGrossSalary ? form.get('salary_amount') : undefined
  const salaryFrequencyField = canSeeGrossSalary ? form.get('salary_frequency') : undefined
  const salaryFrequency =
    !salaryFrequencyField ||
    salaryFrequencyField.value === '' ||
    salaryFrequencyField.value === null
      ? undefined
      : salaryFrequencyField.value
  const isSalarySectionFocused = isAmountFieldFocused || isFrequencyFieldFocused
  const isFrench =
    contractVersion && type === 'fix' ? contractVersion.isFrench : legalEntity.country === 'fr'

  if (loading) {
    return (
      <Box
        gap="s8"
        padding="s48"
        alignItems="center"
        data-testid="additional-compensations-loading"
      >
        <Spinner size="s32" data-testid="loading" />
      </Box>
    )
  }

  return (
    <Box gap="s48">
      {!isNewAdditionalCompensationsSupportEnabled && canSeePayrollPolicy && (
        <PayrollPolicy
          translations={translations}
          canEdit={canEditPayrollPolicy}
          type={type}
          step={step}
          withFormWrapper={withFormWrapper}
        />
      )}
      {canSeeGrossSalary && (
        <FormWrapper
          withFormWrapper={withFormWrapper}
          hint={
            step && (
              <SalaryHint
                step={step}
                type={type}
                salaryAmountField={salaryAmountField!}
                salaryFrequencyField={salaryFrequencyField!}
                isSalarySectionFocused={isSalarySectionFocused}
                employee={employee}
                salaryRange={salaryRange}
              />
            )
          }
        >
          <Box gap="s16">
            <Box flexDirection="row" gap="s16">
              <MoneyField
                value={salaryAmountField!.value}
                onChange={(value) => salaryAmountField!.set(value)}
                onFocus={() => setIsAmountFieldFocused(true)}
                onBlur={() => setIsAmountFieldFocused(false)}
                error={getFirstError(salaryAmountField!)}
                label={translate(translations, 'base_salary')}
                currency={employee.attributes.legal_entity_currency}
                mandatory={salaryFrequencyField!.hasValue}
              />
              <Select
                onChange={(value: string) => salaryFrequencyField!.set(value)}
                value={salaryFrequency}
                onFocus={() => setIsFrequencyFieldFocused(true)}
                onBlur={() => setIsFrequencyFieldFocused(false)}
                error={getFirstError(salaryFrequencyField!)}
                label={translate(translations, 'salary_frequency')}
                none={i18n.t('common.choose')}
                options={salaryOptions}
                mandatory={salaryAmountField!.hasValue}
              />
            </Box>
            {isFrench && form.has('fr_coefficient') && (
              <Text
                field={form.get('fr_coefficient')}
                label={i18n.t('contract_versions.forms.gross_salary.fr_coefficient')}
              />
            )}
          </Box>
        </FormWrapper>
      )}
      {canSeeCompensations && step && (
        <AdditionalCompensations
          step={step}
          type={type}
          translations={translations}
          isNewAdditionalCompensationsSupportEnabled={isNewAdditionalCompensationsSupportEnabled}
        />
      )}
    </Box>
  )
}

export default observer(Salary)
